import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Search, Check, Pencil, Trash2, Plus } from 'lucide-react';
import { Modal, Button } from '../../../components';
import carePlannerService from '../../../services/carehome_manager/carePlannerService';
import { FieldLabel, inputClass, errorMessage } from './shared';

/**
 * Resident groups — "Rose Wing", "First floor" — the manager's own way of
 * folding the planner. A group is only a view: deleting one never touches a
 * resident or any planned care, which the dialog says plainly.
 *
 *   request = { kind: 'edit', group? }   add a group, or edit `group`
 *   request = { kind: 'manage' }         list every group
 */

const FALLBACK_COLORS = ['#2563EB', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#EF4444', '#64748B'];

const GroupForm = ({ group, meta, onCancel, onSaved }) => {
  const colors = meta?.group_colors?.length ? meta.group_colors : FALLBACK_COLORS;
  const [name, setName] = useState(group?.name || '');
  const [color, setColor] = useState(group?.color || colors[(meta?.groups?.length || 0) % colors.length]);
  const [selected, setSelected] = useState(() => new Set((group?.residence_ids || []).map(Number)));
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const residents = meta?.residents || [];
  const q = search.trim().toLowerCase();
  const shown = useMemo(() => residents.filter((r) => !q
    || r.name.toLowerCase().includes(q)
    || String(r.room || '').toLowerCase().includes(q)), [residents, q]);

  // Which other group each resident already sits in — a hint, not a rule.
  const elsewhere = useMemo(() => {
    const map = new Map();
    (meta?.groups || []).filter((g) => g.id !== group?.id).forEach((g) => {
      (g.residence_ids || []).forEach((id) => { if (!map.has(Number(id))) map.set(Number(id), g.name); });
    });
    return map;
  }, [meta, group]);

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the group a name.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = { name, color, residence_ids: [...selected] };
    try {
      const res = group?.id
        ? await carePlannerService.updateGroup(group.id, payload)
        : await carePlannerService.createGroup(payload);
      onSaved(res.message || 'Group saved.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <FieldLabel htmlFor="cp-group-name" required>Group name</FieldLabel>
        <input id="cp-group-name" className={inputClass} maxLength={60} placeholder="e.g. Rose Wing" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div>
        <FieldLabel>Colour</FieldLabel>
        <div className="flex gap-2">
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Colour ${c}`}
              aria-pressed={color === c}
              onClick={() => setColor(c)}
              className={`w-7 h-7 rounded-lg grid place-items-center ring-offset-2 dark:ring-offset-[#262b33] ${color === c ? 'ring-2 ring-[#0B1220] dark:ring-gray-200' : ''}`}
              style={{ background: c }}
            >
              {color === c && <Check className="w-3.5 h-3.5 text-white" />}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[12px] font-semibold text-[#334155] dark:text-gray-300">Residents</span>
          <span aria-live="polite" className="text-[11.5px] font-semibold text-blue-600">{selected.size} selected</span>
        </div>
        <div className="flex items-center gap-2 px-3 h-10 rounded-xl border border-[#DCE4EF] dark:border-gray-700 mb-2">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input
            type="search"
            aria-label="Search residents"
            placeholder="Search name or room"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-[13px] text-[#0B1220] dark:text-gray-100 focus:outline-none"
          />
        </div>
        <div className="flex gap-3 mb-2 text-[12px] font-semibold">
          <button type="button" className="text-blue-600 hover:underline" onClick={() => setSelected((s) => new Set([...s, ...shown.map((r) => r.id)]))}>Select all shown</button>
          <button type="button" className="text-blue-600 hover:underline" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
        <div className="max-h-[280px] overflow-y-auto rounded-xl border border-[#E3E9F3] dark:border-gray-800 divide-y divide-[#EDF1F7] dark:divide-gray-800">
          {shown.map((r) => (
            <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#F7F9FC] dark:hover:bg-[#2b3038]">
              <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} className="w-4 h-4 accent-blue-600" />
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-[#0B1220] dark:text-gray-100 truncate">{r.name}</span>
                <span className="block text-[11.5px] text-[#94A3B8]">
                  {r.room ? `Room ${r.room}` : 'No room recorded'}
                  {elsewhere.has(r.id) ? ` · also in ${elsewhere.get(r.id)}` : ''}
                </span>
              </span>
            </label>
          ))}
          {!shown.length && <p className="px-3 py-5 text-[12.5px] text-[#94A3B8]">No residents match your search.</p>}
        </div>
      </div>
      {error && <p role="alert" className="text-[12.5px] font-medium text-rose-600">{error}</p>}
      <div className="flex justify-end gap-2.5 pt-1">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>Save group</Button>
      </div>
    </form>
  );
};

const GroupsModal = ({ request, meta, onClose, onChanged, onEdit }) => {
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);
  const groups = meta?.groups || [];

  const remove = async (group) => {
    setBusy(true);
    try {
      const res = await carePlannerService.deleteGroup(group.id);
      toast.success(res.message || 'Group deleted.');
      setConfirmId(null);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (request.kind === 'edit') {
    return (
      <Modal isOpen onClose={onClose} title={request.group?.id ? 'Edit resident group' : 'Add resident group'} maxWidth="max-w-lg">
        <GroupForm
          group={request.group}
          meta={meta}
          onCancel={onClose}
          onSaved={(message) => {
            toast.success(message);
            onChanged();
            onClose();
          }}
        />
      </Modal>
    );
  }

  return (
    <Modal isOpen onClose={onClose} title="Manage groups" maxWidth="max-w-lg">
      <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">Groups fold and filter the planner. Deleting a group keeps its residents and their planned care.</p>
      <div className="space-y-2">
        {groups.map((g) => (
          <div key={g.id} className="rounded-xl border border-[#E3E9F3] dark:border-gray-800 px-3.5 py-3">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: g.color || '#94A3B8' }} />
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{g.name}</span>
                <span className="block text-[11.5px] text-[#94A3B8]">{(g.residence_ids || []).length} resident{(g.residence_ids || []).length === 1 ? '' : 's'}</span>
              </span>
              <Button size="sm" variant="ghost" icon={<Pencil className="w-3.5 h-3.5" />} aria-label={`Edit ${g.name}`} onClick={() => onEdit(g)} />
              <Button size="sm" variant="danger-soft" icon={<Trash2 className="w-3.5 h-3.5" />} aria-label={`Delete ${g.name}`} onClick={() => setConfirmId(g.id)} />
            </div>
            {confirmId === g.id && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2 rounded-lg bg-rose-50 dark:bg-rose-900/15 px-3 py-2 text-[12px] text-rose-700 dark:text-rose-300">
                <span className="flex-1">Delete “{g.name}”?</span>
                <Button size="sm" variant="secondary" onClick={() => setConfirmId(null)}>Keep</Button>
                <Button size="sm" variant="danger" loading={busy} onClick={() => remove(g)}>Delete</Button>
              </div>
            )}
          </div>
        ))}
        {!groups.length && <p className="text-[12.5px] text-[#94A3B8] py-4 text-center">No groups yet.</p>}
      </div>
      <div className="flex justify-between gap-2.5 mt-5">
        <Button variant="soft" leftIcon={<Plus className="w-4 h-4" />} onClick={() => onEdit(null)}>Add group</Button>
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
};

export default GroupsModal;
